import React, { FC, useState } from 'react';

// Components
import { Layout } from '../../components/Layout';
import { Button } from '../../components/Button';
import SummarySection from '../../components/Checkout/SummarySection';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Heading } from '../../components/Heading';
import { InputCheckbox } from '../../components/InputCheckbox';

// Context
import { setFormScene } from '../../context/form-context/actions';
import { useForm } from '../../context/form-context/hooks';

// Hooks
import useSummaryAnswers from '../../hooks/useSummaryAnswers';

// Interfaces
import { Summary } from '../../interfaces/Summary';

// i18n
import { useTranslation } from '../../translations/useTranslation';
import { TranslationKey } from '../../translations';

interface Props {
  isLoading: boolean;
  onSubmit: () => Promise<void>;
  submissionErrorKey: TranslationKey | null;
}

export const CheckoutView: FC<Props> = ({
  isLoading,
  submissionErrorKey,
  onSubmit,
}) => {
  const summary = useSummaryAnswers();
  const { dispatch } = useForm();
  const { t, m, keys } = useTranslation();
  const [consent, setConsent] = useState<boolean>(false); // consent for leaving correct information
  const [consentError, setConsentError] = useState<string | undefined>();
  const [isValid, setIsValid] = useState<boolean>(false);
  const handleOnBack = () => {
    if (dispatch) {
      dispatch(setFormScene('DETAILS'));
    }
  };
  const handleOnConsentChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { checked } = event.target;
    setConsent(checked);
    if (consentError) {
      setConsentError(undefined);
    }
  };
  const validateConsent = () => {
    return new Promise<boolean>((resolve, reject) => {
      if (consent) {
        setConsentError(undefined);
        setIsValid(true);
        resolve(true);
      } else if (!consent) {
        setConsentError(t(keys.errorConsentRequired));
        setIsValid(false);
        reject(false);
      }
      return reject(false);
    });
  };
  const handleOnSubmit = () => {
    Promise.all([validateConsent()])
      .then(() => onSubmit())
      .catch(() => {});
  };

  return (
    <Layout viewId={'checkout'}>
      <h1 className="text-2xl margin-bottom-2xs">
        {t(keys.checkoutSummaryHeader)}
      </h1>
      <p className="margin-bottom-xs text-md">
        {t(keys.checkoutSummarySubHeader)}
      </p>
      <div className="flex flex-column gap-xs">
        {summary.map((section: Summary) => (
          <SummarySection key={section.title} {...section} />
        ))}
      </div>
      <div className="border-top border-contrast-low padding-y-sm margin-top-xs">
        <InputCheckbox
          label={t(keys.submissionConsent)}
          name={'correct-information'}
          value={'correct'}
          checked={consent}
          errorMessage={consentError}
          onChange={handleOnConsentChange}
        />
      </div>

      <div className="flex flex-wrap gap-2xs margin-bottom-xs">
        <Button
          isLoading={isLoading}
          onClick={handleOnSubmit}
          title={t(keys.uiSubmit)}
        />
        <Button
          layout="secondary"
          disabled={isLoading}
          onClick={handleOnBack}
          title={t(keys.uiButtonGoBack)}
        />
      </div>

      {submissionErrorKey && (
        <p className="bg-error-high border border-error padding-y-3xs text-sm padding-x-2xs margin-top-2xs radius-sm">
          {t(submissionErrorKey)}
        </p>
      )}
    </Layout>
  );
};
